import { styled } from '@mui/material/styles';
import { Landing } from '../Landing/index';
import { type LandingPageType } from '@Src/middlewares/CustomPageTypes';
import Highlight from '../Highlight';
import { OrderSettings } from '@Components/Widgets/OrderSettings';
import Apps from '@Components/Widgets/Apps';
import MenuHighlights from '../Menu';
import Testimonials from '@Components/Widgets/Testimonials';
import Gallery from '@Components/Widgets/Gallery';
import OrderStatus from '@Components/Widgets/OrderStatus';
import Reorder from '@Components/Widgets/Reorder';
import { LogInTextButton } from '../LogIn';
import LocationsWidget from '@Components/Widgets/Locations';
import Voucher from '@Components/Widgets/Voucher';
import Grid from '@mui/material/Grid';
import { InnerGridPolice } from '../BaseGridElements';

const StyledLandingChildrenWrapper = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

const getContainerFlexDirection = (
  isCollection: boolean,
  hasUserDeliveryAddress: boolean,
  hasMoreThanOneCollectionStore: boolean,
): string => {
  if (
    (isCollection && !hasMoreThanOneCollectionStore) ||
    (!isCollection && hasUserDeliveryAddress)
  ) {
    return 'column';
  } else if (
    (isCollection && hasMoreThanOneCollectionStore) ||
    (!isCollection && !hasUserDeliveryAddress)
  ) {
    return 'row';
  } else {
    return 'column';
  }
};

const StyledOrderSettingsContainer = styled('div')<{
  ownerState: {
    isCollection: boolean;
    hasUserDeliveryAddress: boolean;
    hasMoreThanOneCollectionStore: boolean;
  };
}>(({ ownerState, theme }) => ({
  width: '100%',
  minHeight: '156px',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    minHeight: '120px',
    flexDirection: getContainerFlexDirection(
      ownerState.isCollection,
      ownerState.hasUserDeliveryAddress,
      ownerState.hasMoreThanOneCollectionStore,
    ),
    marginTop: theme.spacing(-9.5),
    position: 'relative',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(-3.5),
  },
}));

const getLogInStyles = (
  isCollection: boolean,
  hasUserDeliveryAddress: boolean,
  hasMoreThanOneCollectionStore: boolean,
): { alignItems: string; marginTop: string | number; width: string } => {
  if (
    (isCollection && !hasMoreThanOneCollectionStore) ||
    (!isCollection && hasUserDeliveryAddress)
  ) {
    return { alignItems: 'center', marginTop: '32px', width: '100%' };
  }
  if (isCollection && hasMoreThanOneCollectionStore) {
    return { alignItems: 'flex-end', marginTop: 0, width: '100%' };
  }
  if (!isCollection && !hasUserDeliveryAddress) {
    return { alignItems: 'flex-end', marginTop: 0, width: '55%' };
  }
  return { alignItems: 'center', marginTop: '32px', width: '100%' };
};

const StyledLogInContainer = styled('div')<{
  ownerState: {
    isCollection: boolean;
    hasUserDeliveryAddress: boolean;
    hasMoreThanOneCollectionStore: boolean;
  };
}>(({ ownerState, theme }) => {
  const { alignItems, marginTop, width } = getLogInStyles(
    ownerState.isCollection,
    ownerState.hasUserDeliveryAddress,
    ownerState.hasMoreThanOneCollectionStore,
  );
  return {
    alignItems,
    display: 'flex',
    flexDirection: 'column-reverse',
    marginTop,
    width,
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      flexDirection: 'column',
      marginTop: theme.spacing(4),
      width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  };
});

export const OrderSettingsGrid = styled('div')(({ theme }) => ({
  margin: `0 ${theme.spacing(3)}`,
  [theme.breakpoints.up('sm')]: {
    margin: `0 ${theme.spacing(5)}`,
  },
}));

export const Dishy = ({
  data,
  isLoggedIn,
  hasMoreThanOneCollectionStore,
  hasUserDeliveryAddress,
  isCollection,
}: {
  data: LandingPageType;
  hasMoreThanOneCollectionStore: boolean;
  hasUserDeliveryAddress: boolean;
  isCollection: boolean;
  isLoggedIn: boolean;
}): JSX.Element => {
  let headingText = '';
  if (isLoggedIn) {
    headingText = data?.loggedInHeading || '';
  } else {
    headingText = data?.loggedOutHeading || '';
  }
  const hideWelcomeMessage = !data.brandNameHeadingEnabled && !headingText;
  return (
    <>
      <Landing
        backgroundImages={data?.backgroundImages}
        hideWelcomeMessage={hideWelcomeMessage}
      >
        <StyledLandingChildrenWrapper>
          <OrderStatus />
          <Voucher />
        </StyledLandingChildrenWrapper>
      </Landing>
      <OrderSettingsGrid>
        <StyledOrderSettingsContainer
          ownerState={{
            hasMoreThanOneCollectionStore,
            hasUserDeliveryAddress,
            isCollection,
          }}
        >
          <OrderSettings />
          {!isLoggedIn && (
            <StyledLogInContainer
              ownerState={{
                hasMoreThanOneCollectionStore,
                hasUserDeliveryAddress,
                isCollection,
              }}
            >
              <LogInTextButton />
            </StyledLogInContainer>
          )}
        </StyledOrderSettingsContainer>
      </OrderSettingsGrid>
      <Reorder />
      <InnerGridPolice shouldOverFlowMobile>
        <MenuHighlights />
      </InnerGridPolice>
      {data.highlight?.enabled && <Highlight data={data?.highlight} />}
      {data.testimonialsEnabled && (
        <InnerGridPolice shouldOverFlowMobile>
          <Testimonials />
        </InnerGridPolice>
      )}
      {data.mobileAppsEnabled && <Apps />}
      {data.galleryEnabled && (
        <InnerGridPolice shouldOverFlowMobile>
          <Gallery />
        </InnerGridPolice>
      )}
      {data.locationsEnabled && (
        <InnerGridPolice>
          <LocationsWidget showSubTitle showTitle />
        </InnerGridPolice>
      )}
    </>
  );
};

export default Dishy;
